/**
 * Central SEO config: per-route title, description, canonical, og:image.
 * Used by useSEO hook to update document head on route change.
 */
export const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://www.apkakhata.com';

/** Default OG/Twitter image (absolute URL). */
export const DEFAULT_OG_IMAGE = `${SITE_URL}/logo/ak_official.webp`;
const DEFAULT_OG_IMAGE_ALT = 'ApkaKhata - Business Solutions';

const DEFAULT_TITLE = 'ApkaKhata - Complete Business Solutions | Website Development & Software';
const DEFAULT_DESCRIPTION = 'Professional website development, business management software, and AI solutions. Custom websites, hotel & restaurant systems, salon management for your business.';

export const SEO_ROUTES = {
  '/': {
    title: 'ApkaKhata - Professional Website Development & Business Solutions',
    description: 'Professional website development and custom management systems for businesses of all sizes. Websites, hotel management, restaurant POS, salon software, AI solutions – India.',
    keywords: 'website development, business management systems, hotel management, restaurant management, salon software, digital solutions, custom software, AI solutions, India',
  },
  '/products': {
    title: 'Our Products - Website, Hotel, Restaurant & AI Solutions | ApkaKhata',
    description: 'Explore our products: website development, hotel management, restaurant POS, salon & spa software, school ERP, AI chatbots, and business management solutions.',
    keywords: 'website development, hotel management software, restaurant POS, salon management, AI chatbot, business solutions, India',
  },
  '/website': {
    title: 'Website Packages - Professional Websites for Business | ApkaKhata',
    description: 'Get a professional, responsive website for your business. Essential to Enterprise packages. SEO-friendly, fast, with CMS and payment options.',
    keywords: 'website packages, business website, responsive website, CMS, e-commerce website, India',
  },
  '/services': {
    title: 'Business & AI Services | ApkaKhata',
    description: 'AI/ML services, business management solutions, hotel and restaurant systems, digital solutions for your business.',
    keywords: 'AI services, business management, hotel management, restaurant management, digital solutions',
  },
  '/pricing': {
    title: 'Pricing - Website & Software Packages | ApkaKhata',
    description: 'Transparent pricing for website packages and business management software. Essential, Professional, Business Plus, and custom solutions.',
    keywords: 'website pricing, software pricing, business packages, India',
  },
  '/pricing/website-packages': {
    title: 'Website Packages - Pricing & Plans | ApkaKhata',
    description: 'Compare website packages: Essential, Professional, Business Plus, Business Elite, Enterprise. Transparent pricing, features, and support.',
    keywords: 'website packages, pricing plans, business website cost, India',
  },
  '/contact': {
    title: 'Contact Us - Get in Touch | ApkaKhata',
    description: 'Contact ApkaKhata for website development, business software, and digital solutions. Get a free consultation.',
    keywords: 'contact ApkaKhata, website quote, software consultation',
  },
  '/faq': {
    title: 'FAQ - Frequently Asked Questions | ApkaKhata',
    description: 'Common questions about website development, business management software, packages, payments, and support.',
    keywords: 'FAQ, website development FAQ, business software FAQ',
  },
  '/referral': {
    title: 'Referral & Discount Codes | ApkaKhata',
    description: 'Use a referral code for discounts on website and software packages. Share your code and earn rewards.',
    keywords: 'referral code, discount code, ApkaKhata referral',
  },
  '/purchase': {
    title: 'Purchase Website Package | ApkaKhata',
    description: 'Complete your website package purchase. Secure checkout and payment options for Essential, Professional, Business Plus, Business Elite, and Enterprise.',
    keywords: 'purchase website, buy website package, ApkaKhata checkout',
  },
  '/service': {
    title: 'Services - Website & Business Solutions | ApkaKhata',
    description: 'Professional website development, hotel management, restaurant systems, AI solutions, and custom business software.',
    keywords: 'website development services, business software services, India',
  },
  '/blogs': {
    title: 'Business & Tech Blog - Growth, AI & Digital Solutions | ApkaKhata',
    description: 'Read the latest articles on business growth, AI automation, digital transformation, and professional website development for Indian businesses.',
    keywords: 'business blog, tech blog, AI articles, digital transformation stories, website development tips',
  },
};

/** Package slug -> short description for meta (used for /website/:slug). */
export const PACKAGE_SEO = {
  essential: {
    title: 'Essential Website Package - Single Page | ApkaKhata',
    description: 'Starter website package for small businesses. Single-page, mobile-responsive, WhatsApp & contact. From ₹14,999.',
    keywords: 'essential website package, single page website, small business website, India',
  },
  professional: {
    title: 'Professional Website Package - Multi Page | ApkaKhata',
    description: 'Multi-page website with video gallery, portfolio, SEO. Works like an app. From ₹24,999.',
    keywords: 'professional website package, multi page website, business website, India',
  },
  'business-plus': {
    title: 'Business Plus Website - CMS & Blog | ApkaKhata',
    description: 'Update your website yourself. Admin panel, blog, portfolio, 12 months support. Best value. From ₹44,999.',
    keywords: 'business plus website, CMS website, self-manageable website, India',
  },
  'business-elite': {
    title: 'Business Elite - E-commerce & Payments | ApkaKhata',
    description: 'Accept payments on your website. UPI, cards, wallets. E-commerce ready. From ₹74,999.',
    keywords: 'business elite, e-commerce website, online payments, India',
  },
  enterprise: {
    title: 'Enterprise - Custom Software & Solutions | ApkaKhata',
    description: 'Custom web apps, management systems, hotels, restaurants. Built for your business. Custom pricing.',
    keywords: 'enterprise software, custom development, hotel management, restaurant system, India',
  },
};

/**
 * Paths that must not be indexed (dashboards, checkout, payment).
 */
const NOINDEX_PATHS = [
  '/admin-dashboard',
  '/owner',
  '/partner',
  '/profile',
  '/checkout',
  '/payment',
  '/security-deposit-payment',
  '/security-deposit-success',
];

export function isNoIndexPath(pathname) {
  const path = pathname.replace(/\/$/, '') || '/';
  return NOINDEX_PATHS.some((p) => path === p || path.startsWith(p + '/'));
}

/** Policy slug -> title and description for legal pages */
const LEGAL_SEO = {
  terms: { title: 'Terms & Conditions | ApkaKhata', description: 'Terms and conditions for using ApkaKhata website and services.' },
  privacy: { title: 'Privacy Policy | ApkaKhata', description: 'Privacy policy: how ApkaKhata collects, uses and protects your data.' },
  'refund-policy': { title: 'Refund & Cancellation Policy | ApkaKhata', description: 'Refund and cancellation policy for ApkaKhata website and software packages.' },
  'service-delivery-policy': { title: 'Service Delivery Policy | ApkaKhata', description: 'Service delivery and timeline policy for ApkaKhata.' },
  about: { title: 'About Us | ApkaKhata', description: 'About ApkaKhata - Professional website development and business solutions.' },
  disclaimer: { title: 'Disclaimer | ApkaKhata', description: 'Disclaimer and limitations of ApkaKhata services.' },
  'pricing-payment-policy': { title: 'Pricing & Payment Policy | ApkaKhata', description: 'Pricing and payment policy for ApkaKhata packages.' },
};

/**
 * Get SEO config for a pathname (exact or base path). 
 * @param {string} pathname Current path
 * @param {any[]} blogsData Optional blogs data for dynamic blog SEO
 */
export function getSEOForPath(pathname, blogsData = []) {
  const path = pathname.replace(/\/$/, '') || '/';
  const exact = SEO_ROUTES[path];
  if (exact) return { ...exact, ogImage: exact.ogImage || DEFAULT_OG_IMAGE, ogImageAlt: exact.ogImageAlt || DEFAULT_OG_IMAGE_ALT };

  if (path.startsWith('/blogs/')) {
    const slug = path.split('/')[2];
    const blog = blogsData.find(b => b.id === slug);
    if (blog) {
      return {
        title: `${blog.title} | ApkaKhata Blog`,
        description: blog.excerpt || blog.title,
        keywords: `${blog.category}, business growth, digital presence`,
        ogImage: blog.image ? (blog.image.startsWith('http') ? blog.image : `${SITE_URL}${blog.image}`) : DEFAULT_OG_IMAGE,
        ogImageAlt: blog.title,
        blog: blog // Pass blog data for schema
      };
    }
    return { ...SEO_ROUTES['/blogs'], ogImage: DEFAULT_OG_IMAGE, ogImageAlt: DEFAULT_OG_IMAGE_ALT };
  }

  if (path.startsWith('/website/')) {
    const slug = path.split('/')[2];
    const packageSeo = slug && PACKAGE_SEO[slug];
    if (packageSeo) {
      return { ...packageSeo, ogImage: DEFAULT_OG_IMAGE, ogImageAlt: DEFAULT_OG_IMAGE_ALT };
    }
    return { ...SEO_ROUTES['/website'], ogImage: DEFAULT_OG_IMAGE, ogImageAlt: DEFAULT_OG_IMAGE_ALT };
  }
  if (path.startsWith('/services/')) return { ...SEO_ROUTES['/services'], ogImage: DEFAULT_OG_IMAGE, ogImageAlt: DEFAULT_OG_IMAGE_ALT };
  if (path.startsWith('/pricing/')) return { ...SEO_ROUTES['/pricing'], ogImage: DEFAULT_OG_IMAGE, ogImageAlt: DEFAULT_OG_IMAGE_ALT };
  if (path.startsWith('/referral')) return { ...SEO_ROUTES['/referral'], ogImage: DEFAULT_OG_IMAGE, ogImageAlt: DEFAULT_OG_IMAGE_ALT };
  if (path.startsWith('/purchase/')) return { ...SEO_ROUTES['/purchase'], ogImage: DEFAULT_OG_IMAGE, ogImageAlt: DEFAULT_OG_IMAGE_ALT };
  if (path.startsWith('/service')) return { ...SEO_ROUTES['/service'], ogImage: DEFAULT_OG_IMAGE, ogImageAlt: DEFAULT_OG_IMAGE_ALT };

  if (path.startsWith('/legal/')) {
    const policySlug = path.split('/')[2];
    const legal = policySlug && LEGAL_SEO[policySlug];
    return {
      title: legal ? legal.title : 'Legal & Policies | ApkaKhata',
      description: legal ? legal.description : DEFAULT_DESCRIPTION,
      keywords: 'legal, policy, terms, privacy, ApkaKhata',
      ogImage: DEFAULT_OG_IMAGE,
      ogImageAlt: DEFAULT_OG_IMAGE_ALT,
    };
  }

  return { title: DEFAULT_TITLE, description: DEFAULT_DESCRIPTION, keywords: '', ogImage: DEFAULT_OG_IMAGE, ogImageAlt: DEFAULT_OG_IMAGE_ALT };
}
